import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
interface SlideLifecycleProps {
  onContinue: () => void;
}
export function SlideLifecycle({
  onContinue
}: SlideLifecycleProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 600); // axes
    const t2 = setTimeout(() => setStage(2), 1400); // main curve
    const t3 = setTimeout(() => setStage(3), 4000); // phase labels + bands
    const t4 = setTimeout(() => setStage(4), 5800); // potential area
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, []);
  // SVG layout
  // viewBox 1000x480
  // zero line y = 290 (positive above, negative below)
  // R&D    x: 80 → 380   (10 years, dips below zero)
  // Launch x: 380 → 460  (2 years, rapid rise through zero)
  // Mature x: 460 → 730  (8 years, peak/plateau)
  // Legacy x: 730 → 960  (2-10 years, decline back to zero)
  // Main S-curve — Legacy drops as a CLIFF right at start, then flattens to a long thin tail just above zero
  const mainCurve = 'M 80 290 C 140 290 170 380 230 380 C 290 380 340 340 380 290 C 410 250 430 130 460 90 C 540 70 650 70 730 90 C 745 95 760 245 785 275 C 850 283 920 287 960 290';
  // Optimized "potential" — parallel offset RIGHT during the cliff, UP during the tail (preserves higher revenue)
  const potentialCurve = 'M 730 90 C 775 100 790 245 815 268 C 875 265 935 267 985 268';
  // Hatched band: between actual cliff (below+left) and potential (above+right) — green sits ABOVE white on the tail
  const potentialArea = 'M 730 90 C 775 100 790 245 815 268 C 875 265 935 267 985 268 L 960 290 C 920 287 850 283 785 275 C 760 245 745 95 730 90 Z';
  const phases = [{
    x: 230,
    label: 'R&D',
    sub: '10 years',
    xStart: 80,
    xEnd: 380,
    color: 'white/55'
  }, {
    x: 420,
    label: 'Launch',
    sub: '2 years',
    xStart: 380,
    xEnd: 460,
    color: 'white/55'
  }, {
    x: 595,
    label: 'Mature',
    sub: '8 years',
    xStart: 460,
    xEnd: 730,
    color: 'white/55'
  }, {
    x: 845,
    label: 'Legacy',
    sub: '2–10 years',
    xStart: 730,
    xEnd: 960,
    color: 'teal-400',
    accent: true
  }];
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto relative" data-id="element-382">
      {/* Snakker logo top-right */}
      <div className="absolute top-6 right-8 md:top-10 md:right-12 flex items-center gap-2 z-10" data-id="element-383">
        <div className="w-8 h-8 rounded-lg bg-teal-400 flex items-center justify-center" data-id="element-384">
          <div className="w-3.5 h-3.5 rounded-full bg-white" data-id="element-385" />
        </div>
        <span className="text-teal-400 font-bold text-base tracking-tight" data-id="element-386">
          snakker
        </span>
      </div>

      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-3xl md:text-5xl font-serif text-slate-900 mb-4 max-w-3xl" data-id="element-387">
        The Product Journey &{' '}
        <span className="text-teal-400" data-id="element-388">The Untapped Potential</span>
      </motion.h2>

      <div className="flex-1 relative flex items-center justify-center" data-id="element-389">
        <div className="w-full max-w-5xl relative" style={{
        aspectRatio: '2.2 / 1'
      }} data-id="element-390">
          <svg viewBox="0 0 1000 480" className="w-full h-full overflow-visible" data-id="element-391">
            <defs data-id="element-392">
              <pattern id="hatch" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)" data-id="element-393">
                <line x1="0" y1="0" x2="0" y2="8" stroke="#00D4AA" strokeWidth="1.5" strokeOpacity="0.4" data-id="element-394" />
              </pattern>
            </defs>

            {/* + and − axis labels */}
            <motion.text x="60" y="100" textAnchor="middle" className="fill-white/35 text-xs font-medium" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} data-id="element-395">
              +
            </motion.text>
            <motion.text x="60" y="385" textAnchor="middle" className="fill-white/35 text-xs font-medium" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} data-id="element-396">
              −
            </motion.text>

            <motion.text x="30" y="240" textAnchor="middle" className="fill-white/40 text-xs font-medium" style={{
            writingMode: 'vertical-rl',
            textOrientation: 'mixed'
          }} initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} data-id="element-397">
              Cumulative Value
            </motion.text>
            <motion.text x="520" y="455" textAnchor="middle" className="fill-white/40 text-xs font-medium" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} data-id="element-398">
              Time
            </motion.text>

            {/* Y axis */}
            <motion.line x1="80" y1="40" x2="80" y2="430" stroke="#3A5278" strokeWidth="2" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 1 ? 1 : 0
          }} transition={{
            duration: 0.6
          }} data-id="element-399" />

            {/* Zero line (the X axis through the middle) */}
            <motion.line x1="80" y1="290" x2="970" y2="290" stroke="#3A5278" strokeWidth="2" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 1 ? 1 : 0
          }} transition={{
            duration: 0.8
          }} data-id="element-400" />
            <motion.text x="975" y="295" className="fill-white/30 text-[10px] font-medium" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} transition={{
            delay: 0.4
          }} data-id="element-401">
              €0
            </motion.text>

            {/* Phase dividers (light) */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 0.5 : 0
          }} transition={{
            duration: 0.5
          }} data-id="element-402">
              <line x1="380" y1="50" x2="380" y2="430" stroke="#3A5278" strokeWidth="1" strokeDasharray="4,4" data-id="element-403" />
              <line x1="460" y1="50" x2="460" y2="430" stroke="#3A5278" strokeWidth="1" strokeDasharray="4,4" data-id="element-404" />
              <line x1="730" y1="50" x2="730" y2="430" stroke="#3A5278" strokeWidth="1" strokeDasharray="4,4" data-id="element-405" />
            </motion.g>

            {/* Highlight band on Legacy phase when potential reveals */}
            <motion.rect x="730" y="50" width="230" height="380" fill="#00D4AA" fillOpacity="0.04" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 0.6
          }} data-id="element-406" />

            {/* Potential hatched area — Legacy phase reveal */}
            <motion.path d={potentialArea} fill="url(#hatch)" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 1.0
          }} data-id="element-407" />
            <motion.path d={potentialArea} fill="rgba(0, 212, 170, 0.08)" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 1.0
          }} data-id="element-408" />

            {/* Main lifecycle curve — now dark Snakker ink */}
            <motion.path d={mainCurve} fill="none" stroke="#0f172a" strokeWidth="3" strokeOpacity="0.95" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 2 ? 1 : 0
          }} transition={{
            duration: 2.6,
            ease: 'easeInOut'
          }} data-id="element-409" />

            {/* Optimized potential dashed extension */}
            <motion.path d={potentialCurve} fill="none" stroke="#00D4AA" strokeWidth="3" strokeDasharray="8,4" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 1.4,
            ease: 'easeInOut'
          }} data-id="element-410" />

            {/* Phase labels with year durations */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 1 : 0
          }} transition={{
            duration: 0.5
          }} data-id="element-411">
              {phases.map(p => <g key={p.label} data-id="element-412">
                  <text x={p.x} y="450" textAnchor="middle" className={`text-sm font-semibold ${p.accent ? 'fill-teal-400' : 'fill-white/65'}`} data-id="element-413">
                    {p.label}
                  </text>
                  <text x={p.x} y="468" textAnchor="middle" className={`text-xs ${p.accent ? 'fill-teal-300/60' : 'fill-white/35'}`} data-id="element-414">
                    {p.sub}
                  </text>
                </g>)}
            </motion.g>

            {/* POTENTIAL label */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 0.6,
            delay: 0.6
          }} data-id="element-415">
              <line x1="845" y1="215" x2="900" y2="178" stroke="#00D4AA" strokeWidth="1" strokeOpacity="0.6" data-id="element-416" />
              <text x="908" y="170" className="fill-teal-400 text-sm font-bold tracking-wider" data-id="element-417">
                POTENTIAL
              </text>
              <text x="908" y="186" className="fill-teal-300/60 text-[10px]" data-id="element-418">
                Softening the cliff
              </text>
            </motion.g>

            {/* Investment annotation during R&D */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 0.7 : 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} data-id="element-419">
              <text x="230" y="410" textAnchor="middle" className="fill-white/40 text-[10px] italic" data-id="element-420">
                Investment
              </text>
            </motion.g>

            {/* Peak Revenue annotation during Mature */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 0.7 : 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} data-id="element-421">
              <text x="595" y="55" textAnchor="middle" className="fill-white/40 text-[10px] italic" data-id="element-422">
                Peak Revenue
              </text>
            </motion.g>
          </svg>

          {/* Floating insight card on potential reveal */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: stage >= 4 ? 1 : 0,
          y: stage >= 4 ? 0 : 20
        }} transition={{
          duration: 0.5,
          delay: 0.7
        }} className="absolute bottom-2 left-0 bg-white/95 border border-teal-400/30 shadow-lg p-4 rounded-xl max-w-sm backdrop-blur-sm" data-id="element-423">
            <h4 className="text-teal-400 font-semibold mb-1 text-sm" data-id="element-424">
              The Strategic Opportunity
            </h4>
            <p className="text-sm text-slate-700 leading-relaxed" data-id="element-425">
              In the Legacy phase, the dashed teal line shows what's possible
              with continuous optimization — slowing the decline and extending
              the revenue tail by even a few percentage points.
            </p>
          </motion.div>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 4 ? 1 : 0
    }} className="mt-4 flex justify-end" data-id="element-426">
        <Button onClick={onContinue} data-id="element-427">Market Size</Button>
      </motion.div>
    </div>;
}